import { motion } from 'framer-motion';
import { ArrowLeft, Coins, Search, Wallet } from 'lucide-react';
import NetworkBackground from '@/components/clusterscope/NetworkBackground';
import type { Dataset } from '@/data/clusterscope-data';
import { useState } from 'react';

const easeOut: [number, number, number, number] = [0.16, 1, 0.3, 1];

interface AnalysisModeScreenProps {
  dataset: Dataset;
  onBack: () => void;
  onScanToken: (label: string) => void;
  onScanWallet: (address: string) => void;
}

export default function AnalysisModeScreen({ dataset, onBack, onScanToken, onScanWallet }: AnalysisModeScreenProps) {
  const [tokenValue, setTokenValue] = useState('');
  const [walletValue, setWalletValue] = useState('');

  return (
    <div className="relative flex min-h-screen w-full flex-col overflow-hidden bg-background">
      <div className="pointer-events-none absolute inset-0 bg-grid-pattern opacity-[0.3]" />
      <NetworkBackground density={30} />
      <div className="pointer-events-none absolute left-1/4 top-0 h-[440px] w-[440px] rounded-full bg-[#a78bfa] opacity-[0.1] blur-[130px]" />
      <div className="pointer-events-none absolute right-1/4 bottom-0 h-[440px] w-[440px] rounded-full bg-[#14e0b0] opacity-[0.1] blur-[130px]" />

      <div className="relative z-10 flex flex-1 flex-col px-4 py-10 sm:py-14">
        <button
          onClick={onBack}
          className="absolute left-4 top-8 inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground sm:left-8"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back
        </button>

        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: easeOut }}
          className="mb-12 text-center text-[11px] font-semibold uppercase tracking-[0.25em] text-muted-foreground"
        >
          Choose Analysis Mode
        </motion.div>

        <div className="mx-auto flex w-full max-w-4xl flex-1 flex-col items-center justify-center gap-6 md:flex-row md:items-start md:gap-0">
          {/* Token Scanner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1, ease: easeOut }}
            whileHover={{ y: -4 }}
            className="glass group relative w-full max-w-md rounded-2xl border border-white/[0.08] bg-white/[0.03] p-7 backdrop-blur-xl transition-shadow duration-300 hover:border-[#a78bfa]/40 hover:shadow-[0_12px_50px_-12px_rgba(167,139,250,0.35)] md:max-w-none"
          >
            <div className="mb-5 flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#a78bfa]/15 text-[#a78bfa]">
                <Coins className="h-4.5 w-4.5" />
              </span>
              <span className="rounded-full border border-[#a78bfa]/40 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-[#a78bfa]">
                Token Scanner
              </span>
            </div>

            <h2 className="text-xl font-bold text-foreground">Map Cluster Activity</h2>
            <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">
              Paste a token mint to reveal coordinated wallet clusters, timing correlations, and
              wash-trading rings operating on the token.
            </p>

            <div className="mt-5 flex items-stretch gap-2">
              <input
                value={tokenValue}
                onChange={(e) => setTokenValue(e.target.value)}
                placeholder="Token mint or ticker..."
                className="flex-1 rounded-lg border border-white/10 bg-black/30 px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 outline-none focus:border-[#a78bfa]/50"
              />
              <button
                onClick={() => onScanToken(tokenValue.trim() || dataset.tryTokens[0])}
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#a78bfa] px-4 py-2.5 text-sm font-bold text-[#160f2b] transition-transform hover:scale-[1.03] active:scale-95"
              >
                <Search className="h-3.5 w-3.5" />
                Scan
              </button>
            </div>

            <div className="mt-4 flex gap-2">
              {dataset.tryTokens.map((t) => (
                <button
                  key={t}
                  onClick={() => onScanToken(t)}
                  className="rounded-full border border-white/10 px-3 py-1 text-xs text-muted-foreground transition-colors hover:border-[#a78bfa]/50 hover:text-foreground"
                >
                  Try {t}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Divider */}
          <div className="hidden shrink-0 flex-col items-center justify-center px-6 md:flex">
            <span className="h-16 w-px bg-white/10" />
            <span className="my-3 text-xs font-medium uppercase tracking-widest text-muted-foreground/60">
              or
            </span>
            <span className="h-16 w-px bg-white/10" />
          </div>
          <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-muted-foreground/60 md:hidden">
            <span className="h-px w-10 bg-white/10" /> or <span className="h-px w-10 bg-white/10" />
          </div>

          {/* Wallet Profiler */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.18, ease: easeOut }}
            whileHover={{ y: -4 }}
            className="glass group relative w-full max-w-md rounded-2xl border border-white/[0.08] bg-white/[0.03] p-7 backdrop-blur-xl transition-shadow duration-300 hover:border-[#14e0b0]/40 hover:shadow-[0_12px_50px_-12px_rgba(20,224,176,0.35)] md:max-w-none"
          >
            <div className="mb-5 flex items-center gap-3">
              <span className="flex h-9 w-9 rotate-45 items-center justify-center rounded-md bg-[#14e0b0]/15 text-[#14e0b0]">
                <Wallet className="h-4 w-4 -rotate-45" />
              </span>
              <span className="rounded-full border border-[#14e0b0]/40 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-[#14e0b0]">
                Wallet Profiler
              </span>
            </div>

            <h2 className="text-xl font-bold text-foreground">Analyze a Wallet</h2>
            <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">
              Paste a Solana wallet address for a full profitability breakdown, win rate, hold
              times, and cluster affiliation report.
            </p>

            <div className="mt-5 flex items-stretch gap-2">
              <input
                value={walletValue}
                onChange={(e) => setWalletValue(e.target.value)}
                placeholder="Enter Solana wallet address..."
                className="flex-1 rounded-lg border border-white/10 bg-black/30 px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 outline-none focus:border-[#14e0b0]/50"
              />
              <button
                onClick={() => onScanWallet(walletValue.trim() || dataset.tryWallets[0])}
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#14e0b0] px-4 py-2.5 text-sm font-bold text-[#04140f] transition-transform hover:scale-[1.03] active:scale-95"
              >
                <Search className="h-3.5 w-3.5" />
                Scan
              </button>
            </div>

            <div className="mt-4 flex gap-2">
              {dataset.tryWallets.map((w) => (
                <button
                  key={w}
                  onClick={() => onScanWallet(w)}
                  className="rounded-full border border-white/10 px-3 py-1 text-xs text-muted-foreground transition-colors hover:border-[#14e0b0]/50 hover:text-foreground"
                >
                  Try {w}
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
